<template>
  <div
    :id="isLinkedResource ? sectionId : `view-${sectionId}`"
    ref="sectionContainer"
    :class="[
      'section-container',
      {
        'is-dsr': isDsr,
      },
    ]"
  >
    <div v-if="isDsr" :class="['margin-top', { 'is-preview': isPreview }]"></div>

    <ConditionOverlay
      v-if="showConditionOverlay({ sectionId, contentId: contentId ?? '' })"
      data-external="condition-select-effect-section-button"
      section
      :selectable="!hasSectionCondition"
      :disabled-tooltip="$t('conditional_logc_element_select_disabled_tooltip')"
      @select="selectEffect(conditionalContentSelection)"
    />
    <section
      :class="[
        'section',
        {
          'first-row-is-cover-image': EditorHelper.rowContainsCoverSize(rows[0], NodeType.Image),
          'last-row-is-cover-image': EditorHelper.rowContainsCoverSize(
            rows[rows.length - 1],
            NodeType.Image
          ),
          'is-hidden': isHidden,
          'transparent-background': branding?.backgroundColor === 'transparent',
        },
      ]"
      :style="branding?.backgroundColor ? { backgroundColor: branding.backgroundColor } : {}"
    >
      <DetachedCommentAnchor
        v-for="comment in commentsWithoutRowId"
        :id="comment.id"
        :key="comment.id"
      />
      <template v-for="(row, index) in filteredRows">
        <EditorSideBySideRow
          v-if="row.type === RowType.SideBySide"
          :key="row.id"
          :cells="row.cells"
          :theme="theme"
          :merge-values="mergeValues"
          :is-pdf-viewer="isPdfViewer"
          :block-width-class="blockWidthClass"
          :class="{ 'side-by-side-sibling': EditorHelper.isPreviousRowSideBySide(index, rows) }"
          :page-width="pageWidth"
          :comments="CommentsHelper.filterCommentsByRow(comments, row.id)"
          :selected-comment-id="selectedCommentId"
          :show-contextual-comments="showContextualComments"
          :create-contextual-comment-button-state="createContextualCommentButtonState"
          @create-comment="$emit('create-comment', { ...$event, rowId: row.id })"
          @select-comment="$emit('select-comment', $event)"
          @track-click="$emit('track-click', $event)"
          @observe-node-visibility="$emit('observe-node-visibility', $event)"
          @fetch-new-image="
            $emit('fetch-new-image', { ...$event, targetSectionId: sectionId, targetRowId: row.id })
          "
        />
        <EditorRowColumns
          v-else-if="row.type === RowType.Columns"
          :key="row.id"
          :block-width-class="blockWidthClass"
          :cells="row.cells"
          :column-count="row.columnCount"
          :is-pdf-viewer="isPdfViewer"
          :merge-values="mergeValues"
          :page-width="pageWidth"
          :comments="CommentsHelper.filterCommentsByRow(comments, row.id)"
          :selected-comment-id="selectedCommentId"
          :show-contextual-comments="showContextualComments"
          :create-contextual-comment-button-state="createContextualCommentButtonState"
          @create-comment="$emit('create-comment', { ...$event, rowId: row.id })"
          @select-comment="$emit('select-comment', $event)"
          @observe-node-visibility="$emit('observe-node-visibility', $event)"
          @fetch-new-image="
            $emit('fetch-new-image', { ...$event, targetSectionId: sectionId, targetRowId: row.id })
          "
        />
        <EditorRowSlideShow
          v-else-if="row.type === RowType.SlideShow"
          :key="row.id"
          :block-width-class="blockWidthClass"
          :cells="row.cells"
          :row-id="row.id"
          :column-count="row.columnCount"
          :is-pdf-viewer="isPdfViewer"
          :is-preview="isPreview"
          :page-width="pageWidth"
          @observe-node-visibility="$emit('observe-node-visibility', $event)"
          @fetch-new-image="
            $emit('fetch-new-image', { ...$event, targetSectionId: sectionId, targetRowId: row.id })
          "
        />
        <EditorRow
          v-else
          :key="row.id"
          :class="[{ 'first-row': index === 0 }, { 'last-row': row === rows[rows.length - 1] }]"
          :cells="row.cells"
          :context="context"
          :merge-values="mergeValues"
          :focused-required-id="focusedRequiredId"
          :is-pdf-viewer="isPdfViewer"
          :block-width-class="blockWidthClass"
          :contains-cover-image="EditorHelper.rowContainsCoverSize(row, NodeType.Image)"
          :contains-cover-video="EditorHelper.rowContainsCoverSize(row, NodeType.Video)"
          :page-width="pageWidth"
          :partially-signed-fields="partiallySignedFields"
          :sender="sender"
          :pusher-client="pusherClient"
          :recipient="recipient"
          :allowed-to-edit-after-partially-signed="allowedToEditAfterPartiallySigned"
          :recipient-input="recipientInput"
          :updating-inputs="updatingInputs"
          :failed-input-updates="failedInputUpdates"
          :comments="CommentsHelper.filterCommentsByRow(comments, row.id)"
          :selected-comment-id="selectedCommentId"
          :show-contextual-comments="showContextualComments"
          :create-contextual-comment-button-state="createContextualCommentButtonState"
          :current-user="currentUser"
          :linked-contracts="linkedContracts"
          :mapped-placeholders="mappedPlaceholders"
          @update-input-value="$emit('update-input-value', $event)"
          @track-video="$emit('track-video', $event)"
          @observe-node-visibility="$emit('observe-node-visibility', $event)"
          @partially-sign="$emit('partially-sign', $event)"
          @create-comment="$emit('create-comment', { ...$event, rowId: row.id })"
          @select-comment="$emit('select-comment', $event)"
          @fetch-new-image="
            $emit('fetch-new-image', { ...$event, targetSectionId: sectionId, targetRowId: row.id })
          "
        />
      </template>
      <div v-if="isHidden" class="hidden-overlay">
        <FriedIcon icon="view-hide" />
      </div>
    </section>
    <div v-if="isDsr" class="bottom-padding"></div>
  </div>
</template>
<script setup lang="ts">
import type { Comment as DrComment } from '@getaccept/dsr-shared';
import type {
  ContractListItem,
  EBlockWidth,
  MappedContentPlaceholder,
  MergeValues,
  PartiallySignedEvent,
  Row,
  SectionColors,
} from '@getaccept/editor-lib';
import {
  Context,
  EditorHelper,
  NodeType,
  PlaceholderHelper,
  RowType,
  useObserveElement,
  setBrandingColors,
  mergeSectionBrandingWithEntityTheme,
  setInputFieldPlaceholderTextColor,
  useProvideBrandingState,
} from '@getaccept/editor-lib';
import { useConditionalLogicStore } from '@getaccept/lib-shared/src/conditional-logic/store/conditional-logic.store';
import DetachedCommentAnchor from '@getaccept/lib-shared/src/contextual-commenting/components/DetachedCommentAnchor.vue';
import * as CommentsHelper from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type { CreateContextualCommentButtonState } from '@getaccept/lib-shared/src/contextual-commenting/types/create-contextual-comment-button-state';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { PusherClient } from '@getaccept/lib-shared/src/pusher/pusher-client';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ConditionOverlay from '@getaccept/lib-shared/src/conditional-logic/components/ConditionOverlay.vue';
import type { EntityTheme } from '@getaccept/lib-shared/src/branding/types/theme';
import type { ConditionalContentSectionSelection } from '@getaccept/lib-shared/src/conditional-logic/types/selection-payload';
import type { ProseMirrorSelection } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import EditorRow from './EditorRow.vue';
import EditorRowColumns from './EditorRowColumns.vue';
import EditorRowSlideShow from './EditorRowSlideShow.vue';
import EditorSideBySideRow from './EditorSideBySideRow.vue';

const {
  context = Context.Document,
  sectionId,
  contentId = '',
  rows = [],
  comments = [],
  mergeValues,
  isDsr,
  isEditPreview,
  theme,
  branding,
} = defineProps<{
  context?: Context;
  focusedRequiredId?: string;
  sectionId: string;
  contentId?: string;
  rows?: Row[];
  mergeValues?: MergeValues;
  isPdfViewer?: boolean;
  isDsr?: boolean;
  isPreview?: boolean;
  isHidden?: boolean;
  blockWidthClass?: EBlockWidth;
  pageWidth?: number;
  sender?: User;
  theme: EntityTheme;
  recipient?: Recipient;
  recipientInput?: EditorRecipientInput[];
  updatingInputs?: EditorRecipientInput[];
  failedInputUpdates?: EditorRecipientInput[];
  partiallySignedFields?: PartiallySignedEvent[];
  pusherClient?: PusherClient;
  comments?: (Comment | DrComment)[];
  selectedCommentId?: string;
  showContextualComments?: boolean;
  createContextualCommentButtonState?: CreateContextualCommentButtonState;
  currentUser?: User;
  linkedContracts?: ContractListItem[];
  mappedPlaceholders?: MappedContentPlaceholder[];
  allowedToEditAfterPartiallySigned?: boolean;
  isLinkedResource?: boolean;
  isEditPreview?: boolean;
  branding?: SectionColors;
}>();

const emit = defineEmits<{
  'create-comment': [
    {
      textSelection: ProseMirrorSelection;
      content: string;
      nodeId: string;
      cellId: string;
      rowId: string;
    },
  ];
  'select-comment': [string];
  'track-click': [{ nodeId: string }];
  'observe-node-visibility': [{ nodeId: string; visible: boolean }];
  'fetch-new-image': [
    {
      targetNodeId: string;
      imageId: string;
      targetCellId: string;
      targetRowId: string;
      targetSectionId: string;
    },
  ];
  'partially-sign': [{ nodeId: string }];
  'track-video': [{ nodeId: string }];
  'update-input-value': [{ id: string; value: string | undefined }];
  'update-element-visibility': [{ visible: boolean }];
}>();

const filteredRows = computed(() =>
  rows.filter(row =>
    [Context.DsrTemplate, Context.DsrResource, Context.DsrPreview].includes(context) ||
    isEditPreview
      ? true
      : PlaceholderHelper.filterRowForPlaceholders(row)
  )
);

const sectionContainer = ref<HTMLElement | null>(null);

const { observe, disconnect } = useObserveElement({
  onObserve: ({ visible }) => {
    emit('update-element-visibility', { visible });
  },
});

const initializeIntersectionObserver = async () => {
  await nextTick();
  if (!sectionContainer.value) {
    return;
  }
  observe(sectionContainer.value);
};

const rowIds = computed(() => rows.map(row => row.id));

const commentsWithoutRowId = computed(() =>
  comments.filter(comment => !rowIds.value.includes(comment.editorSelection?.rowId ?? ''))
);
const conditionalLogicStore = useConditionalLogicStore();
const {
  getConditionalEffectBySelection,
  getUpdatedEffectsFromSelection,
  selectEffect,
  showConditionOverlay,
} = conditionalLogicStore;

const conditionalContentSelection = computed<ConditionalContentSectionSelection>(() => ({
  contentId: contentId ?? '',
  sectionId: sectionId,
}));

const hasSectionCondition = computed(
  () =>
    !!getConditionalEffectBySelection(conditionalContentSelection.value) ||
    !!getUpdatedEffectsFromSelection(conditionalContentSelection.value)?.length
);

const brandingColors = computed(() => mergeSectionBrandingWithEntityTheme(theme, branding));

watch(
  [brandingColors, sectionContainer],
  () => {
    if (!sectionContainer.value || !brandingColors.value) return;
    setBrandingColors(sectionContainer.value, brandingColors.value);
    setInputFieldPlaceholderTextColor(
      sectionContainer.value,
      brandingColors.value.inputField.backgroundColor
    );
  },
  { immediate: true, deep: true }
);

useProvideBrandingState({
  branding: brandingColors,
});

onMounted(() => {
  if (!isDsr) {
    return;
  }
  initializeIntersectionObserver();
});

onBeforeUnmount(() => {
  if (!isDsr) {
    return;
  }
  disconnect();
});
</script>
<style lang="scss" scoped>
@import '@getaccept/editor-lib/src/scss/editor-section';

section {
  &.hidden {
    opacity: 0.7;
  }
}
</style>
